import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Gavel as BidIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import { InvoiceWithRelations, BidCreate, Bid, BidStatus, Company, ScoreGrade } from '../../types/models';
import { formatCurrency, formatDate, formatDateOnly, getDaysRemaining, getDueStatusText } from '../../utils/formatters';
import { InvoiceStatus } from '../../types/models';
import ScoreBadge from '../../components/invoices/ScoreBadge';
import ScoreBreakdown from '../../components/invoices/ScoreBreakdown';

const MarketplaceInvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const { user } = useAuth();
  
  const [invoice, setInvoice] = useState<InvoiceWithRelations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Bid dialog
  const [openBidDialog, setOpenBidDialog] = useState(false);
  const [bidAmount, setBidAmount] = useState<string>('');
  const [bidLoading, setBidLoading] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);
  const [bidSuccess, setBidSuccess] = useState(false);
  
  // User's existing bids on this invoice
  const [userBids, setUserBids] = useState<Bid[]>([]);
  
  const fetchInvoice = useCallback(async () => {
    if (!invoiceId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get<InvoiceWithRelations>(`/invoices/${invoiceId}`);
      setInvoice(response);

      // If issuer relation is missing but issuer_id is present, fetch issuer company
      if (!response.issuer && response.issuer_id) {
        try {
          const issuerCompany = await apiService.get<Company>(`/companies/${response.issuer_id}`);
          setInvoice(prev => (prev ? { ...prev, issuer: issuerCompany } : prev));
        } catch (issuerErr) {
          console.error('Error fetching issuer company:', issuerErr);
        }
      }

      // Fetch all bids for this invoice and filter for the current user
      if (user) {
        const bidsResponse = await apiService.get<Bid[]>(`/invoices/${invoiceId}/bids`);
        const currentUserBids = bidsResponse.filter(bid => bid.buyer_id === user.id);
        setUserBids(currentUserBids);
      }
    } catch (err: any) {
      console.error('Error fetching invoice details:', err);
      setError(err.response?.data?.detail || 'Failed to load invoice details');
    } finally {
      setLoading(false);
    }
  }, [invoiceId, user]);
  
  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);
  
  const handleOpenBidDialog = () => {
    // Don't allow bidding on sold invoices
    if (invoice?.status === InvoiceStatus.SOLD || invoice?.status === 'sold') {
      return;
    }
    
    setOpenBidDialog(true);
    setBidAmount('');
    setBidError(null);
    setBidSuccess(false);
  };
  
  const handleCloseDialog = () => {
    setOpenBidDialog(false);
    // If bid was successful, refresh the invoice data
    if (bidSuccess) {
      fetchInvoice();
    }
  };
  
  const handleBidAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setBidAmount(value);
    }
  };
  
  const handleSubmitBid = async () => {
    if (!invoice) return;
    
    // Validate bid amount
    const numericBidAmount = parseFloat(bidAmount);
    if (isNaN(numericBidAmount) || numericBidAmount <= 0) {
      setBidError('Ingrese un monto de oferta válido');
      return;
    }

    if (invoice.highest_bid != null && numericBidAmount <= invoice.highest_bid) {
      setBidError(`Su oferta debe ser mayor que la oferta más alta actual de ${formatCurrency(invoice.highest_bid, invoice.currency)}`);
      return;
    }
    
    if (numericBidAmount >= invoice.net_amount) {
      setBidError('El monto de la oferta debe ser menor que el monto neto de la factura');
      return;
    }
    
    if (invoice.minimum_amount != null && numericBidAmount < invoice.minimum_amount) {
      setBidError(`La oferta debe ser mayor o igual al monto mínimo de ${formatCurrency(invoice.minimum_amount, invoice.currency)}`);
      return;
    }
    
    setBidLoading(true);
    setBidError(null);
    
    try {
      const bidData: BidCreate = {
        invoice_id: invoice.id,
        amount: numericBidAmount
      };
      
      await apiService.post('/bids', bidData);
      setBidSuccess(true);
    } catch (err: any) {
      console.error('Error placing bid:', err);
      setBidError(err.response?.data?.detail || 'Failed to place bid');
    } finally {
      setBidLoading(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  if (!invoice) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning">Invoice not found.</Alert>
      </Container>
    );
  }

  const daysRemaining = getDaysRemaining(invoice.due_date);
  const dueStatus = getDueStatusText(daysRemaining);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/marketplace">
          Marketplace
        </Link>
        <Typography color="text.primary">Invoice #{invoice.invoice_number}</Typography>
      </Breadcrumbs>
      
      <Grid container spacing={4}>
        {/* Left Column - Invoice Details & Bidding */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>Detalles de la Factura</Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Monto Neto</Typography>
                <Typography variant="h6" color="primary.main">{formatCurrency(invoice.net_amount, invoice.currency)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Fecha de Vencimiento</Typography>
                <Typography variant="body1">{formatDateOnly(invoice.due_date)}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Oferta Más Alta</Typography>
                <Typography variant="h6" color="secondary.main">
                  {invoice.highest_bid != null ? formatCurrency(invoice.highest_bid, invoice.currency) : 'Sin ofertas aún'}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Estado de Vencimiento</Typography>
                <Chip label={dueStatus.text} color={dueStatus.color} size="small" />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Estado de la Factura</Typography>
                <Chip 
                  label={(typeof invoice.status === 'string' ? invoice.status : String(invoice.status)).toUpperCase()} 
                  color={invoice.status === InvoiceStatus.SOLD || invoice.status === 'sold' ? 'success' : 
                         invoice.status === InvoiceStatus.VERIFIED || invoice.status === 'verified' ? 'info' : 'default'} 
                  size="small" 
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Industria</Typography>
                <Typography variant="body1">{invoice.debtor?.industry || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Moneda</Typography>
                <Typography variant="body1">{invoice.currency}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Timbrado</Typography>
                <Typography variant="body1">{invoice.timbrado || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Monto Mínimo</Typography>
                <Typography variant="body1">{invoice.minimum_amount != null ? formatCurrency(invoice.minimum_amount, invoice.currency) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="subtitle2" color="text.secondary">Rating</Typography>
                {invoice.effective_grade ? (
                  <ScoreBadge
                    grade={invoice.effective_grade as ScoreGrade}
                    score={invoice.effective_score ?? undefined}
                    size="small"
                    overridden={invoice.score_source === 'override'}
                  />
                ) : (
                  <Typography variant="body1" color="text.disabled">N/A</Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {invoice.effective_grade && (
            <Box sx={{ mb: 3 }}>
              <ScoreBreakdown invoiceId={invoice.id} />
            </Box>
          )}

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Sus Ofertas</Typography>
            {userBids.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userBids.map((bid) => (
                      <TableRow key={bid.id}>
                        <TableCell>{formatDate(bid.created_at)}</TableCell>
                        <TableCell align="right">{formatCurrency(bid.amount, invoice.currency)}</TableCell>
                        <TableCell>
                          <Chip label={bid.status} color={bid.status === BidStatus.ACCEPTED ? 'success' : 'default'} size="small" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1" color="text.secondary">
                Usted no ha realizado ofertas en esta factura aún.
              </Typography>
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleOpenBidDialog}
                startIcon={<BidIcon />}
                disabled={invoice.status === InvoiceStatus.SOLD || invoice.status === 'sold'}
              >
                {invoice.status === InvoiceStatus.SOLD || invoice.status === 'sold' ? 'Factura Vendida' : 'Realizar Nueva Oferta'}
              </Button>
            </Box>
          </Paper>

          {user?.role === 'buyer' && (
            <Card>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={8}>
                    <Typography variant="h6">¿Está listo para realizar una oferta?</Typography>
                    <Typography variant="body2" color="text.secondary">
                      La oferta más alta actual es <strong>{invoice.highest_bid != null ? formatCurrency(invoice.highest_bid, invoice.currency) : 'N/A'}</strong>.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4} sx={{ textAlign: 'right' }}>
                    <Button 
                      variant="contained" 
                      color="secondary" 
                      startIcon={<BidIcon />}
                      onClick={handleOpenBidDialog}
                      disabled={invoice.status === InvoiceStatus.SOLD || invoice.status === 'sold'}
                    >
                      {invoice.status === InvoiceStatus.SOLD || invoice.status === 'sold' ? 'Factura Vendida' : 'Realizar Oferta'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right Column - Debtor & Seller Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Información del Deudor</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Nombre del Deudor
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {invoice.debtor ? invoice.debtor.name : 'Deudor Desconocido'}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              País
            </Typography>
            <Typography variant="body1">
              {invoice.debtor ? invoice.debtor.country : 'N/A'}
            </Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Información del Vendedor</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="subtitle2" color="text.secondary">
              Nombre del Vendedor
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {invoice.seller ? invoice.seller.full_name : 'Vendedor Desconocido'}
            </Typography>
            
            {invoice.company && (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  Compañía
                </Typography>
                <Typography variant="body1">
                  {invoice.company.name}
                </Typography>
              </>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Información del Emisor</Typography>
            <Divider sx={{ mb: 2 }} />
            {invoice.issuer ? (
              <>
                <Typography variant="subtitle2" color="text.secondary">
                  Nombre del Emisor
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {invoice.issuer.name}
                </Typography>
                <Typography variant="subtitle2" color="text.secondary">
                  RUC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {invoice.issuer.tax_id}
                </Typography>
                {invoice.issuer.address && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary">Dirección</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{invoice.issuer.address}</Typography>
                  </>
                )}
              </>
            ) : (
              <Typography variant="body1" color="text.secondary">Información del emisor no disponible</Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Bid Dialog */}
      <Dialog open={openBidDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>Realizar Oferta</DialogTitle>
        <DialogContent>
          {bidSuccess ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Su oferta ha sido realizada con éxito!
            </Alert>
          ) : (
            <>
              {bidError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {bidError}
                </Alert>
              )}
              
              <DialogContentText sx={{ mb: 2 }}>
                Usted está realizando una oferta en la factura #{invoice.invoice_number}
              </DialogContentText>
              
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Monto Neto de la Factura
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(invoice.net_amount, invoice.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Oferta Más Alta
                    </Typography>
                    <Typography variant="h6" color="secondary.main">
                      {invoice.highest_bid != null ? formatCurrency(invoice.highest_bid, invoice.currency) : 'Sin ofertas aún'}
                    </Typography>
                  </Grid>
                  {invoice.minimum_amount != null && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Monto Mínimo Requerido
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(invoice.minimum_amount, invoice.currency)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <TextField
                autoFocus
                margin="dense"
                id="bid-amount"
                label="Monto de su Oferta"
                type="text"
                fullWidth
                variant="outlined"
                value={bidAmount}
                onChange={handleBidAmountChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MoneyIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {invoice.currency}
                    </InputAdornment>
                  ),
                }}
                helperText={`Ingrese un monto mayor que la oferta más alta actual`}
                disabled={bidLoading}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: '16px 24px' }}>
          <Button onClick={handleCloseDialog}>
            {bidSuccess ? 'Cerrar' : 'Cancelar'}
          </Button>
          {!bidSuccess && (
            <Button 
              onClick={handleSubmitBid} 
              variant="contained" 
              disabled={bidLoading}
              startIcon={bidLoading ? <CircularProgress size={20} /> : <BidIcon />}
            >
              {bidLoading ? 'Realizando Oferta...' : 'Realizar Oferta'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MarketplaceInvoiceDetail;
